import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Plus, X, Pencil, Trash2, Check, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const LEVELS = [1, 2, 3, 4];

export default function CompetencyMapEditor() {
  const { user } = useAuth();
  const isSuperAdmin = user.system_role === "super_admin";
  const [cmap, setCmap] = useState([]);
  const [skills, setSkills] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    const [cmapRes, skillsRes] = await Promise.all([
      api.get("/competency-map"),
      api.get("/skills"),
    ]);
    setCmap(cmapRes.data);
    setSkills(skillsRes.data);
  };

  useEffect(() => { load(); }, []);

  const skillByCode = useMemo(
    () => Object.fromEntries(skills.map((s) => [s.code, s])),
    [skills]
  );

  const filteredRoles = cmap.filter((c) => c.role.toLowerCase().includes(search.toLowerCase()));

  const toggleExpand = (role) => {
    const next = new Set(expanded);
    next.has(role) ? next.delete(role) : next.add(role);
    setExpanded(next);
  };

  const deleteRole = async (role) => {
    try {
      await api.delete(`/competency-map/${encodeURIComponent(role)}`);
      toast.success(`Role "${role}" deleted`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="competency-map-editor-page">
      <PageHeader
        eyebrow="Maintenance"
        title="Competency Map Editor"
        subtitle="Define required competency levels per role. Add roles, attach skills, and tune required levels — no CSV re-import needed."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Competency Map" }]}
        action={
          <div className="flex items-center gap-2">
            <input
              data-testid="role-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search role…"
              className="border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm bg-white"
            />
            <button
              type="button"
              data-testid="add-role-button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1.5 bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm px-3 py-2 font-medium"
            >
              <Plus size={14} /> Add role
            </button>
          </div>
        }
      />

      <div className="px-4 md:px-10 py-8">
        <div className="bc-card divide-y divide-[#E5E5E5]" data-testid="role-list">
          {filteredRoles.length === 0 && (
            <div className="px-5 py-10 text-sm text-[#666666] text-center">
              {cmap.length === 0 ? "No roles defined yet. Click 'Add role' to create one." : "No roles match your search."}
            </div>
          )}
          {filteredRoles.map((c) => (
            <RoleRow
              key={c.role}
              cmap={c}
              skills={skills}
              skillByCode={skillByCode}
              expanded={expanded.has(c.role)}
              onToggle={() => toggleExpand(c.role)}
              onDeleteRole={() => setConfirmDelete(c.role)}
              onReload={load}
              canDeleteRole={isSuperAdmin}
            />
          ))}
        </div>
      </div>

      {showCreate && (
        <CreateRoleModal
          skills={skills}
          existingRoles={cmap.map((c) => c.role)}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteRoleModal
          role={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteRole(confirmDelete)}
        />
      )}
    </div>
  );
}

function RoleRow({ cmap, skills, skillByCode, expanded, onToggle, onDeleteRole, onReload, canDeleteRole }) {
  const skillEntries = Object.entries(cmap.skills || {});
  const [adding, setAdding] = useState(false);

  return (
    <div data-testid={`role-row-${cmap.role}`}>
      <div className="px-5 py-3 flex items-center justify-between bc-row-hover">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 text-left flex-1"
          data-testid={`role-toggle-${cmap.role}`}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <div>
            <div className="text-sm font-semibold">{cmap.role}</div>
            <div className="text-xs text-[#666666]">{skillEntries.length} skill{skillEntries.length === 1 ? "" : "s"}</div>
          </div>
        </button>
        <div className="flex items-center gap-2">
          {canDeleteRole && (
            <button
              type="button"
              onClick={onDeleteRole}
              data-testid={`delete-role-${cmap.role}`}
              className="text-xs text-[#C41E1E] hover:underline inline-flex items-center gap-1"
            >
              <Trash2 size={12} /> Delete role
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="px-5 pb-4 pt-1 bg-[#FAFAFA]">
          {skillEntries.length === 0 && (
            <div className="text-xs text-[#666666] italic mb-3">No skills attached yet. Add one below.</div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#666666] border-b border-[#E5E5E5]">
                <th className="py-2">Skill</th>
                <th className="py-2 w-[200px]">Required level</th>
                <th className="py-2 w-[100px]"></th>
              </tr>
            </thead>
            <tbody>
              {skillEntries.map(([code, level]) => (
                <SkillRow
                  key={code}
                  role={cmap.role}
                  code={code}
                  level={level}
                  skill={skillByCode[code]}
                  onChanged={onReload}
                />
              ))}
            </tbody>
          </table>
          {adding ? (
            <AddSkillForm
              role={cmap.role}
              skills={skills.filter((s) => !cmap.skills?.[s.code])}
              onCancel={() => setAdding(false)}
              onAdded={() => { setAdding(false); onReload(); }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              data-testid={`add-skill-${cmap.role}`}
              className="mt-3 text-xs text-[#C41E1E] hover:underline inline-flex items-center gap-1 font-medium"
            >
              <Plus size={12} /> Add skill to this role
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SkillRow({ role, code, level, skill, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [newLevel, setNewLevel] = useState(level);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/competency-map/${encodeURIComponent(role)}/skills/${encodeURIComponent(code)}`, {
        skill_code: code,
        required_level: parseInt(newLevel),
      });
      toast.success(`Updated ${code} to L${newLevel}`);
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/competency-map/${encodeURIComponent(role)}/skills/${encodeURIComponent(code)}`);
      toast.success(`Removed ${code} from ${role}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-b border-[#E5E5E5]" data-testid={`skill-row-${role}-${code}`}>
      <td className="py-2">
        <div className="font-mono text-xs">{code}</div>
        <div className="text-sm">{skill?.name || code}</div>
      </td>
      <td className="py-2">
        {editing ? (
          <select
            value={newLevel}
            onChange={(e) => setNewLevel(e.target.value)}
            data-testid={`level-select-${role}-${code}`}
            className="border border-[#E5E5E5] rounded-sm px-2 py-1 text-sm bg-white"
          >
            {LEVELS.map((l) => <option key={l} value={l}>L{l}</option>)}
          </select>
        ) : (
          <span className="inline-block px-2 py-0.5 bg-[#0D0D0D] text-white font-mono text-xs rounded-sm">L{level}</span>
        )}
      </td>
      <td className="py-2 text-right">
        {editing ? (
          <div className="flex justify-end gap-2">
            <button type="button" onClick={save} disabled={busy} data-testid={`save-level-${role}-${code}`} className="text-xs text-green-700 hover:underline inline-flex items-center gap-1 disabled:opacity-50">
              <Check size={12} /> Save
            </button>
            <button type="button" onClick={() => { setEditing(false); setNewLevel(level); }} className="text-xs text-[#666666] hover:underline">
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(true)} data-testid={`edit-level-${role}-${code}`} className="text-xs text-[#666666] hover:text-[#0D0D0D] inline-flex items-center gap-1">
              <Pencil size={12} /> Edit
            </button>
            <button type="button" onClick={remove} disabled={busy} data-testid={`remove-skill-${role}-${code}`} className="text-xs text-[#C41E1E] hover:underline inline-flex items-center gap-1 disabled:opacity-50">
              <X size={12} /> Remove
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function AddSkillForm({ role, skills, onCancel, onAdded }) {
  const [code, setCode] = useState(skills[0]?.code || "");
  const [level, setLevel] = useState(2);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code) return;
    setBusy(true);
    try {
      await api.put(`/competency-map/${encodeURIComponent(role)}/skills/${encodeURIComponent(code)}`, {
        skill_code: code,
        required_level: parseInt(level),
      });
      toast.success(`Added ${code} (L${level}) to ${role}`);
      onAdded();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  if (skills.length === 0) {
    return (
      <div className="mt-3 text-xs text-[#666666] italic flex items-center gap-2">
        All skills are already attached. <button onClick={onCancel} className="underline">Close</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`add-skill-form-${role}`}>
      <select
        value={code}
        onChange={(e) => setCode(e.target.value)}
        data-testid={`add-skill-select-${role}`}
        className="border border-[#E5E5E5] rounded-sm px-2 py-1 text-sm bg-white"
      >
        {skills.map((s) => <option key={s.code} value={s.code}>{s.code} — {s.name}</option>)}
      </select>
      <select
        value={level}
        onChange={(e) => setLevel(e.target.value)}
        data-testid={`add-skill-level-${role}`}
        className="border border-[#E5E5E5] rounded-sm px-2 py-1 text-sm bg-white"
      >
        {LEVELS.map((l) => <option key={l} value={l}>L{l}</option>)}
      </select>
      <button type="submit" disabled={busy} data-testid={`confirm-add-skill-${role}`} className="bg-[#C41E1E] hover:bg-[#A31818] text-white text-xs rounded-sm px-3 py-1.5 font-medium disabled:opacity-50">
        {busy ? "Adding…" : "Add"}
      </button>
      <button type="button" onClick={onCancel} className="text-xs text-[#666666] hover:underline px-2">
        Cancel
      </button>
    </form>
  );
}

function CreateRoleModal({ skills, existingRoles, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [rows, setRows] = useState([]); // [{code, level}]
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const addRow = () => {
    const used = new Set(rows.map((r) => r.code));
    const next = skills.find((s) => !used.has(s.code));
    if (!next) return;
    setRows([...rows, { code: next.code, level: 2 }]);
  };

  const updateRow = (idx, patch) => {
    setRows(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx) => {
    setRows(rows.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const trimmed = name.trim();
    if (!trimmed) { setErr("Role name is required"); return; }
    if (existingRoles.includes(trimmed)) { setErr(`Role "${trimmed}" already exists`); return; }
    const skillsMap = Object.fromEntries(rows.map((r) => [r.code, parseInt(r.level)]));
    setBusy(true);
    try {
      await api.post("/competency-map", { role: trimmed, skills: skillsMap });
      toast.success(`Role "${trimmed}" created`);
      onCreated();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="create-role-modal">
      <div className="bg-white rounded-sm shadow-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">Create new role</h3>
          <button type="button" onClick={onClose} className="text-[#666666] hover:text-[#0D0D0D]"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="bc-label block mb-1">Role name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
              data-testid="new-role-name"
              placeholder="e.g. Production Supervisor"
              className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="bc-label">Required competencies</label>
              <button type="button" onClick={addRow} disabled={rows.length >= skills.length} data-testid="new-role-add-skill" className="text-xs text-[#C41E1E] hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                <Plus size={12} /> Add skill
              </button>
            </div>
            {rows.length === 0 && <div className="text-xs text-[#666666] italic">You can attach skills later too.</div>}
            <div className="space-y-2">
              {rows.map((r, idx) => {
                const usedCodes = new Set(rows.map((x, i) => i === idx ? null : x.code).filter(Boolean));
                const available = skills.filter((s) => !usedCodes.has(s.code));
                return (
                  // Row identity is tied to its position because rows have no
                  // stable id in the editor's local state; entries are added
                  // to the end only (no reorder), so a position-based key is
                  // safe here.
                  <div key={`row-${idx}`} className="flex items-center gap-2">
                    <select
                      value={r.code}
                      onChange={(e) => updateRow(idx, { code: e.target.value })}
                      data-testid={`new-role-skill-${idx}`}
                      className="flex-1 border border-[#E5E5E5] rounded-sm px-2 py-1.5 text-sm bg-white"
                    >
                      {available.map((s) => <option key={s.code} value={s.code}>{s.code} — {s.name}</option>)}
                    </select>
                    <select
                      value={r.level}
                      onChange={(e) => updateRow(idx, { level: e.target.value })}
                      data-testid={`new-role-level-${idx}`}
                      className="border border-[#E5E5E5] rounded-sm px-2 py-1.5 text-sm bg-white"
                    >
                      {LEVELS.map((l) => <option key={l} value={l}>L{l}</option>)}
                    </select>
                    <button type="button" onClick={() => removeRow(idx)} className="text-[#666666] hover:text-[#C41E1E]">
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          {err && <div className="text-xs text-[#C41E1E] bg-red-50 border border-red-200 px-3 py-2 rounded-sm">{err}</div>}
          <div className="flex justify-end gap-2 pt-2 border-t border-[#E5E5E5]">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#666666] hover:text-[#0D0D0D]">Cancel</button>
            <button type="submit" disabled={busy} data-testid="submit-new-role" className="bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm px-4 py-2 font-medium disabled:opacity-50">
              {busy ? "Creating…" : "Create role"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmDeleteRoleModal({ role, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="confirm-delete-role-modal">
      <div className="bg-white rounded-sm shadow-lg max-w-md w-full">
        <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center gap-2">
          <AlertTriangle size={16} className="text-[#C41E1E]" />
          <h3 className="font-heading text-lg font-semibold">Delete role</h3>
        </div>
        <div className="px-5 py-4 text-sm text-[#0D0D0D] space-y-2">
          <p>You&apos;re about to delete the role <b>{role}</b>.</p>
          <p className="text-xs text-[#666666]">
            This is blocked if any active employee is still assigned this role. Existing
            assessments and training records are preserved either way.
          </p>
        </div>
        <div className="px-5 py-3 border-t border-[#E5E5E5] flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="text-sm text-[#666666] hover:text-[#0D0D0D] px-3 py-2">Cancel</button>
          <button type="button" onClick={onConfirm} data-testid="confirm-delete-role" className="bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm px-4 py-2 font-medium">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
